import { useMemo, useRef, useState } from 'react';
import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { Navigation } from '../components/Navigation';
import { UploadZone } from '../components/UploadZone';
import { FileCard } from '../components/FileCard';
import { ShareDialog } from '../components/ShareDialog';
import { UploadProgress } from '../components/UploadProgress';
import { useFiles } from '../hooks/useFiles';
import type { FileItem } from '../api/client';

export function DashboardPage() {
  const { filesQuery, deleteMutation, uploadMutation, chunkedUpload } = useFiles();
  const [search, setSearch] = useState('');
  const [shareFileId, setShareFileId] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  const filteredFiles = useMemo(() => {
    const files: FileItem[] = filesQuery.data ?? [];
    return files.filter((file) => file.name.toLowerCase().includes(search.toLowerCase()));
  }, [filesQuery.data, search]);

  const usage = useMemo(
    () => filteredFiles.reduce((sum: number, file: FileItem) => sum + file.size / 1024 / 1024, 0),
    [filteredFiles],
  );

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 lg:flex-row">
      <div className="flex flex-col gap-6 lg:w-72">
        <Sidebar usage={usage} />
        <Navigation />
      </div>
      <div className="flex flex-1 flex-col gap-6">
        <Header onUploadClick={() => uploadInputRef.current?.click()} search={search} onSearchChange={setSearch} />
        <UploadZone
          isUploading={uploadMutation.isPending}
          onFileSelect={(file) => uploadMutation.mutate(file)}
          fileInputRef={uploadInputRef}
        />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredFiles.map((file) => (
            <FileCard key={file.id} file={file} onShare={setShareFileId} onDelete={(id) => deleteMutation.mutate(id)} />
          ))}
        </div>
      </div>

      <ShareDialog fileId={shareFileId} open={Boolean(shareFileId)} onOpenChange={(open) => !open && setShareFileId(null)} />
      
      <UploadProgress
        uploads={chunkedUpload.uploads}
        onResume={chunkedUpload.resumeUpload}
        onCancel={chunkedUpload.cancelUpload}
        onRemove={(uploadId) => chunkedUpload.removeUpload(uploadId)}
      />
    </div>
  );
}
